import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateTabInWorkstream as mutation } from "../modules/CustomDashboardModule/services/DashboardConfigurationService";

/**
 * Creates a callable method to update tab information
 * @param {Object} props - The module's config so this hook knows what to update and read
 * @param {Object} props.selectors - an object containing redux selectors for `tabs`
 * @param {function} props.selectors.tabs - redux selector for tabs
 * @param {function} props.onResponse - action to update the tab in the slice
 * @param {function} props.onLoading - loading state handler
 * @param {function} props.onError - error state handler
 * @returns {Object} That contains a function to be called when updating a tab
 */
export const useUpdateDashboardTabMutation = ({
  selectors: { tabs: tabsSelector },
  onResponse,
  onLoading,
  onError,
}) => {
  const dispatch = useDispatch();
  const tabs = useSelector(tabsSelector);

  const updateDashboardTab = useCallback(
    async (tabId, updatedData) => {
      try {
        const body = {
          id: tabId,
          name: updatedData.tabName,
          description: updatedData.tabDescription,
          showTitleInUppercase: updatedData.showTitleInUppercase,
        };

        dispatch(onLoading(true));
        const updatedTab = await mutation(body);
        dispatch(onResponse({ tabId, updatedTab }));

        return updatedTab;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error updating tab";
        dispatch(onError(errorMessage));
        throw error;
      } finally {
        dispatch(onLoading(false));
      }
    },
    [tabs, dispatch, onResponse, onLoading, onError]
  );

  return { updateDashboardTab };
};
